import React from "react";
import OrderCard from "./OrderCard.js";
import LoadingSpinner from "../LoadingSpinner/loadingSpinner.js";
import { getTranslation } from "../../utils/translate.js";
import { useLanguage } from "../../utils/languageContext.js";

/**
 * OrderList component - Displays list of orders with empty state
 * @param {Object} props - Component props
 * @param {Array} props.orders - Array of orders to display
 * @param {boolean} props.loading - Loading state
 * @param {string} props.activeTab - Active tab
 * @param {Array} props.selectOptions - Translated select options
 * @param {Object} props.selectedOptions - Selected options state
 * @param {Function} props.onStatusChange - Handler for status change
 * @param {Function} props.onViewOrder - Handler for viewing full order
 * @param {Function} props.getOptionStyles - Function to get option styles
 * @returns {JSX.Element} Order list component
 */
const OrderList = ({
    orders,
    loading,
    activeTab,
    selectOptions,
    selectedOptions,
    onStatusChange,
    onViewOrder,
    getOptionStyles
}) => {
    const { language } = useLanguage();

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
            </div>
        );
    }

    if (!orders || orders.length === 0) {
        return (
            <div className="text-center py-12 px-4 bg-white dark:bg-[#161b22] rounded-lg shadow-sm dark:shadow-md border dark:border-[#30363d]">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white dark:bg-[#0d1117] mb-4 border dark:border-[#30363d]">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8 text-gray-400 dark:text-[#8b949e]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                        />
                    </svg>
                </div>
                <p className="text-base font-medium text-gray-500 dark:text-[#b1bac4]">
                    {getTranslation("orderPage.noOrders", language)}
                </p>
            </div>
        );
    }

    return (
        <div>
            {orders
                .filter((order) => order != null && order.status != null) // Filter out null/undefined orders and orders without status
                .map((order, index) => (
                    <OrderCard
                        key={order?.id ?? `order-${index}`}
                        order={order}
                        activeTab={activeTab}
                        selectOptions={selectOptions}
                        selectedOptions={selectedOptions}
                        onStatusChange={onStatusChange}
                        onViewOrder={onViewOrder}
                        getOptionStyles={getOptionStyles}
                    />
                ))}
        </div>
    );
};

export default OrderList;

